import React from 'react';
import {Form} from '../../../../components/form/Form';
import {PROJECT_COMPONENT_TYPE_ID} from '../../../common/constants/SourceConstants';
import {useAssetFormActions} from './AssetUtility';
import {populateDesignFields} from './ScreenForm';
import NestedTable from '../../../../components/form/NestedTable';
import {Row, Text} from '@unthinkable/react-core-components';
import {Tag} from '../../../../components/chip/Chip';
import {MultiTag} from '../../../../components/chip/MultiChip';

const componentFields = ({params}) => {
  const {project, projectlibrary_id} = params;

  const {row} = params;
  const {released_version_id} = row || {};

  const remarksField = released_version_id?._id
    ? 'released_version_id.remarks'
    : 'remarks';

  return {
    name: {
      label: 'Component',
      type: 'text',
      field: 'component',
      required: true,
    },
    parentComponent: {
      label: 'Parent Component',
      field: 'component_id',
      type: 'autoComplete',
      api: `/projectComponents`,
      filter: {
        project_id: project?._id,
        library_version_id: null,
        library_id: projectlibrary_id,
        component_type: {$in: ['component', null]},
      },
      suggestionField: 'component',
      valueField: 'component',
      required: true,
    },
    owner: {
      label: 'Owner',
      field: 'owner_id',
      type: 'autoComplete',
      api: `/projects/${project?._id}/members`,
      suggestionField: 'name',
      secondarySuggestionField: 'email',
      colorField: 'color',
      avatar: true,
      valueField: 'name',
      size: 6,
    },
    designOwner: {
      label: 'Design Owner',
      field: 'design_owner_id',
      type: 'autoComplete',
      api: `/projects/${project?._id}/members`,
      suggestionField: 'name',
      secondarySuggestionField: 'email',
      colorField: 'color',
      avatar: true,
      valueField: 'name',
      size: 6,
    },
    description: {
      label: 'Description',
      type: 'textArea',
      field: 'desc',
      inputProps: {
        minRows: 3,
        maxRows: 6,
      },
    },
    remarks: {
      label: 'Remarks',
      type: 'richText',
      field: remarksField,
      placeholder: 'Write remarks here...',
      minHeight: 200,
    },
  };
};

const PropValueRender = ({row, styles}) => {
  const {type, prop_value} = row;
  return (
    <Row gap={4} style={{alignItems: 'center'}}>
      <Text style={styles?.rowText}>{type}</Text>
      {Array.isArray(prop_value) && prop_value.length
        ? prop_value.map(doc => <Tag title={doc.toString()} value={doc} />)
        : void 0}
    </Row>
  );
};

const PropsNestedTable = props => {
  return (
    <NestedTable
      {...props}
      selection={{
        deleteAction: true,
      }}
      header={({values}) => {
        const {name} = values;
        return {
          title: name ? 'Add Props' : 'Add Props',
          secondaryTitle: name,
        };
      }}
      editFormHeader={({values}) => {
        const {name} = values;
        return {
          title: 'Edit Props',
          secondaryTitle: name,
        };
      }}
      modalProps={{
        size: 'medium',
        height: '80%',
      }}
      inlineAddForm
      addInBottom
      inlineFields={[
        {
          label: 'Name',
          type: 'text',
          field: 'name',
          required: true,
          size: 6,
        },
        {
          label: 'Type',
          type: 'multiText',
          field: 'type',
          size: 6,
        },
      ]}
      fields={[
        {
          label: 'Name',
          type: 'text',
          field: 'name',
          required: true,
        },
        {
          label: 'Description',
          type: 'textArea',
          field: 'description',
          inputProps: {
            minRows: 3,
            maxRows: 6,
          },
        },
        {
          label: 'Type',
          type: 'multiText',
          field: 'type',
          size: 6,
        },
        {
          label: 'Control',
          type: 'text',
          field: 'control',
          size: 6,
        },
        {
          label: 'Options',
          type: 'multiText',
          field: 'options',
        },
        {
          label: 'Default Value',
          type: 'text',
          field: 'default_value',
        },
        {
          label: 'Category',
          type: 'text',
          field: 'category',
        },
      ]}
      columns={[
        {
          header: 'Name',
          type: 'text',
          field: 'name',
        },
        {
          header: 'Type',
          field: 'type',
          width: 120,
          render: ({row}) => {
            return <MultiTag values={row.type} />;
          },
        },
        {
          header: 'Description',
          type: 'text',
          field: 'description',
        },
      ]}
    />
  );
};

const CodeSampleNestedTable = props => {
  return (
    <NestedTable
      {...props}
      selection={{
        deleteAction: true,
      }}
      header={() => {
        return {
          title: 'Add Code Sample',
        };
      }}
      editFormHeader={({values}) => {
        const {title} = values;
        return {
          title: 'Edit Code Sample',
          secondaryTitle: title,
        };
      }}
      modalProps={{
        size: 'medium',
        height: '80%',
      }}
      fields={[
        {
          label: 'Title',
          type: 'text',
          field: 'title',
          required: true,
        },
        {
          label: 'Description',
          type: 'textArea',
          field: 'description',
          inputProps: {
            minRows: 3,
            maxRows: 6,
          },
        },
        {
          field: 'args',
          label: 'Args',
          placeholder: 'Enter arguments here...',
          type: 'textArea',
          inputProps: {
            minRows: 3,
            maxRows: 6,
          },
        },
        {
          field: 'parameters',
          label: 'Parameters',
          placeholder: 'Enter parameters here...',
          type: 'textArea',
          inputProps: {
            minRows: 3,
            maxRows: 6,
          },
        },
        {
          label: 'Code Sample',
          field: 'sample_code',
          placeholder: 'Write your Component code here...',
          type: 'textArea',
          height: 500,
        },
      ]}
      columns={[
        {
          header: 'Title',
          type: 'text',
          field: 'title',
        },
        {
          header: 'Description',
          type: 'text',
          field: 'description',
        },
      ]}
    />
  );
};

export const StyledComponentForm = props => {
  const {
    route: {params},
    mode,
    data,
  } = props;

  const designFields = populateDesignFields({row: data, params});

  const fields = componentFields({params});

  const tabs = {
    basic_info: {
      label: 'Basic Info',
      layoutFields: [
        fields.name,
        fields.parentComponent,
        {
          collapsedFields: [
            fields.owner,
            fields.designOwner,
            fields.description,
          ],
        },
      ],
    },
    designs: {
      label: 'Designs',
      layoutFields: designFields,
    },
    style: {
      label: 'Styles',
      layoutFields: [
        {
          label: 'Style',
          field: 'style',
          type: 'textArea',
          inputProps: {
            minRows: 4,
            maxRows: 8,
          },
        },
      ],
    },
    fields: {
      label: 'Props',
      layoutFields: [
        {
          nested: true,
          field: 'props',
          render: props => <PropsNestedTable {...props} />,
        },
      ],
    },
    code: {
      label: 'Sample Code',
      layoutFields: [
        {
          nested: true,
          field: 'sample_codes',
          render: props => <CodeSampleNestedTable {...props} />,
        },
      ],
    },
    remarks: {
      visible: !!mode,
      label: 'Remarks',
      layoutFields: [fields.remarks],
    },
  };

  return <Form type={'tab'} submitAction="Save" tabs={tabs} {...props} />;
};

export const AddStyledComponentForm = props => {
  const {
    route: {params},
  } = props;

  const {project, feature, model, searchValue, afterSubmit, projectlibrary_id} =
    params;

  const {createAsset, requiredValidation} = useAssetFormActions({
    source: PROJECT_COMPONENT_TYPE_ID,
  });

  const initialValues = {
    status: 'active',
    component: searchValue,
    project_id: project?._id,
    model_id: model?._id,
    source: PROJECT_COMPONENT_TYPE_ID,
    feature_id: feature?._id,
    component_type: 'styled',
  };
  if (projectlibrary_id) {
    initialValues.library_id = projectlibrary_id;
  }

  return (
    <StyledComponentForm
      header="Add Styled Component"
      onSubmit={createAsset}
      afterSubmit={afterSubmit}
      beforeSubmit={requiredValidation}
      defaultValues={initialValues}
      eventSourceId={['Feature', 'StyledComponent']}
      {...props}
    />
  );
};

export const StyledComponentDetailForm = props => {
  const {
    route: {params},
  } = props;

  const {row, feature, readOnly} = params;

  const {updateHistory, assetBeforeSubmit} = useAssetFormActions({
    source: PROJECT_COMPONENT_TYPE_ID,
    feature_id: feature?._id,
    row,
  });

  return (
    <StyledComponentForm
      key={row._id}
      selectedTab={'designs'}
      mode="edit"
      isDualMode
      header={{
        title: 'Styled Component Detail',
        secondaryTitle: row?.component,
      }}
      onSubmit={updateHistory}
      beforeSubmit={assetBeforeSubmit({data: row})}
      readOnly={readOnly}
      data={row}
      {...props}
    />
  );
};
